import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  inject,
  Input,
  type OnChanges,
  ViewChild,
} from '@angular/core';
import Chart from 'chart.js';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import type { ResBody as GetStatsResBody } from '../../../server/routes/super-admin/stats/get';
import { StatsService } from '../stats.service';

dayjs.extend(minMax);

@Component({
  selector: 'app-generate-chart',
  templateUrl: './generate-chart.component.html',
  styleUrls: ['./generate-chart.component.scss'],
  standalone: false,
})
export class GenerateChartComponent implements OnChanges {
  private http = inject(HttpClient);
  private statsService = inject(StatsService);

  @ViewChild('chart1') chart1: ElementRef;
  @Input() endPoint: string;
  @Input() selectedType: string;
  @Input() start: number;
  @Input() end: number;
  @Input() chartLabel: string;
  dataCollection: {
    labels: string[];
    datasets: {
      label: string;
      borderWidth: number;
      backgroundColor: string;
      data: number[];
      // stack: string;
    }[];
  };
  chartColors = {
    red: 'rgb(255, 99, 132)',
    orange: 'rgb(255, 159, 64)',
    yellow: 'rgb(255, 205, 86)',
    green: 'rgb(75, 192, 192)',
    blue: 'rgb(54, 162, 235)',
    purple: 'rgb(153, 102, 255)',
    grey: 'rgb(201, 203, 207)',
  };
  barChart: Chart;

  ngOnChanges(): void {
    this.getChartData();
  }

  private getChartData() {
    this.http
      .get<GetStatsResBody>(this.endPoint, {
        params: {
          start: this.start,
          end: this.end,
        },
      })
      .subscribe({
        next: ({ data }) => {
          this.dataCollection = this.fillChartData(
            data,
            this.selectedType,
            this.chartLabel
          );
          this.createChart();
        },
      });
  }

  private fillChartData(
    chartData: {
      time: number;
      value: number;
    }[],
    selectedType: string,
    chartLabel: string
  ) {
    if (this.barChart) {
      this.barChart.destroy();
    }
    if (selectedType === 'All' || selectedType === 'Custom') {
      const times = chartData.map((i) => i.time);
      this.start = this.start ?? Math.min(...times);
      this.end = this.end ?? Math.max(...times);

      const difference = dayjs(this.end).diff(dayjs(this.start), 'days');
      if (difference > 7 && difference <= 28) {
        this.selectedType = 'Past 4 weeks';
      } else if (difference <= 7) {
        this.selectedType = 'Week-Daily';
      } else if (difference > 28) {
        this.selectedType = 'Year';
      }

      this.dataCollection = this.getDataCollection(
        this.selectedType,
        chartData,
        this.start,
        this.end,
        chartLabel
      );
      return this.dataCollection;
    } else {
      this.dataCollection = this.getDataCollection(
        this.selectedType,
        chartData,
        this.start,
        this.end,
        chartLabel
      );
      return this.dataCollection;
    }
  }

  private getDataCollection(
    selectedType: string,
    chartData: {
      time: number;
      value: number;
    }[],
    start: number,
    end: number,
    chartLabel: string
  ) {
    let labels: string[];

    const {
      times,
      data: [data],
    } = this.statsService.fillOutData([chartData], selectedType, start, end);

    if (selectedType === 'Today') {
      labels = this.statsService.generateTimeLabels(times, 'hour');
    } else if (selectedType === 'Week-Hourly') {
      labels = this.statsService.generateTimeLabels(times, 'hour');
    } else if (selectedType === 'Week-Daily') {
      labels = this.statsService.generateTimeLabels(times, 'day');
    } else if (selectedType === 'Past 4 weeks') {
      labels = this.statsService.generateTimeLabels(times, 'day');
    } else if (selectedType === 'Year') {
      labels = this.statsService.generateTimeLabels(times, 'month');
    } else {
      throw new Error('Unknown selectedType');
    }

    this.dataCollection = {
      labels,
      datasets: [
        {
          label: chartLabel,
          borderWidth: 1,
          backgroundColor: 'rgb(0,191,255)',
          data,
        },
      ],
    };

    return this.dataCollection;
  }

  private createChart() {
    const ctx = this.chart1.nativeElement.getContext('2d');
    this.barChart = new Chart(ctx, {
      type: 'bar',
      data: this.dataCollection,
      options: {
        responsive: true,
        legend: false,
        scales: {
          xAxes: [
            {
              ticks: {
                autoSkip: true,
                maxTicksLimit: 12,
                // labelOffset: 12,
                minRotation: 90,
                maxRotation: 90,
              },
              gridLines: { zeroLineColor: '#b8b8b8' },
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                precision: 0,
              },
              gridLines: { zeroLineColor: '#b8b8b8' },
            },
          ],
        },
        maintainAspectRatio: false,
      },
    });
  }
}
